import { ComputedRef } from 'vue'

import { DailySoluition } from '@/domain/entities/dashboard/dailySolution'
import { User } from '@/domain/entities/user/user'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { SolvedProblemsViewModel } from '@/presentation/viewmodels/dashboard/solvedProblemsViewModel'
import { StatisticsViewModel } from '@/presentation/viewmodels/dashboard/statisticsViewModel'
import { getLineChartOptions } from '@/util/dashboard/lineChartOptions'

export class SolvedProblemsPresenter {
  private viewModel: SolvedProblemsViewModel
  private statisticsViewModel: StatisticsViewModel

  constructor(
    statisticsViewModel: StatisticsViewModel,
    viewModel: SolvedProblemsViewModel = new SolvedProblemsViewModel()
  ) {
    this.viewModel = viewModel
    this.statisticsViewModel = statisticsViewModel
  }

  get chartData(): ComputedRef<{ labelChartData: number[]; dailyTotalChartData: number[][]; maxTotal: number }> {
    return this.viewModel.chartData
  }

  get chartSeries(): ComputedRef<any[]> {
    return this.viewModel.chartSeries
  }

  get chartOptions(): ComputedRef<any> {
    return this.viewModel.chartOptions
  }

  get hasData(): ComputedRef<boolean> {
    return this.viewModel.hasData
  }

  get isLoading(): ComputedRef<boolean> {
    return this.statisticsViewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.statisticsViewModel.error
  }

  initChart = (
    userList: User[],
    color: string,
    otherColor: string,
    displayLanguage: DisplayLanguageType,
    isLightMode: boolean
  ) => {
    const chartData = this.getChartOfDailyTotalData()

    if (chartData.labelChartData.length == 0) {
      this.viewModel.setHasData(false)
      return
    }

    try {
      const chartSeries = []
      for (let i = 0; i < chartData.dailyTotalChartData.length; i++) {
        chartSeries.push(
          this.getChartData(userList[i].id, chartData.dailyTotalChartData[i], i == 0 ? color : otherColor)
        )
      }
      const chartOptions = getLineChartOptions(
        displayLanguage == 'Japanese' ? '解答した総問題数' : 'Solved Problems',
        color,
        chartData.labelChartData,
        isLightMode,
        false
      )
      this.viewModel.setChartData(chartData)
      this.viewModel.setChartSeries(chartSeries)
      this.viewModel.setChartOptions(chartOptions)
      this.viewModel.setHasData(true)
    } catch (e) {
      this.viewModel.setHasData(false)
    }
  }

  private getChartData = (name: string, values: any, color: string) => {
    return {
      name: name,
      data: values,
      color: color
    }
  }

  // チャート用のデータを取得
  getChartOfDailyTotalData = () => {
    const userDaily = this.statisticsViewModel.userDailySolutionList.value

    const labelChartData = this.getDateList(userDaily)
    const dailyTotalChartData: number[][] = []
    let maxTotal = 0

    for (let i = 0; i < userDaily.length; i++) {
      const daily = this.getUserTotalData(userDaily[i], labelChartData)
      dailyTotalChartData.push(daily)

      if (daily.length == 0) dailyTotalChartData[i].push(0)

      maxTotal = Math.max(
        maxTotal,
        daily.map(v => v).reduce((a, b) => Math.max(a, b))
      )
    }

    return { labelChartData, dailyTotalChartData, maxTotal }
  }

  // 特定のユーザーのデータを取得
  getUserTotalData = (daily: DailySoluition[], labelChartData: number[]) => {
    const weeklyRatingChartData: number[] = []

    let dailyIndex = 0
    for (let i = 0; i < labelChartData.length; i++) {
      if (dailyIndex >= daily.length) {
        if (dailyIndex == 0) weeklyRatingChartData.push(0)
        else weeklyRatingChartData.push(daily[dailyIndex - 1].total)
      } else if (daily[dailyIndex].date == labelChartData[i]) {
        weeklyRatingChartData.push(daily[dailyIndex].total)
        dailyIndex++
      } else {
        if (dailyIndex == 0) weeklyRatingChartData.push(0)
        else weeklyRatingChartData.push(daily[dailyIndex - 1].total)
      }
    }

    return weeklyRatingChartData
  }

  // 不足した日付を埋める
  getDateList = (daily: DailySoluition[][]) => {
    const set = new Set<number>([])

    for (let i = 0; i < daily.length; i++) {
      for (let j = 0; j < daily[i].length; j++) {
        set.add(daily[i][j].date)
      }
    }

    const list = Array.from(set)
    list.sort()

    return list
  }
}
